import { Component, HostListener, NgZone, OnDestroy, OnInit, Renderer2 } from '@angular/core';

import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Title } from '@angular/platform-browser';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { LoggedUserClass } from './shared/models/users-class';
import { CommonService } from './shared/services/common.service';
import { LoaddefaultdataService } from './shared/services/loaddefaultdata.service';
import { SwUpdate } from '@angular/service-worker';
import { Observable, Subject, catchError, of, takeUntil, tap, filter, map } from 'rxjs';
import { CommonDefaultsClass } from './shared/models/common-class';
import { CacheService } from './shared/services/cache.service';
import { GlobalService } from './shared/services/global.service';
import { environment } from 'src/environments/environment';
import { AuthService } from './shared/services/auth.service';
import { MatBottomSheet, MatBottomSheetConfig } from '@angular/material/bottom-sheet';
import { BaseService } from './shared/services/base.service';
import { SafeStorageService } from './shared/services/safe-storage.service';
import { ChangeDetectorRef } from '@angular/core';
import { GoogleAnalyticsService } from './shared/services/google-analytics.service';
import { UserActivityService } from './shared/services/user-activity.service';
import { NavigationLoggerService } from './shared/services/navigation-logger.service';
import { SeoService } from './shared/services/seo.service';
import { ViewChild } from '@angular/core';
import { ChatbotComponent } from './shared/components/chatbot/chatbot.component';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  updateAvailable = false;
  private pendingSwUpdate = false; // 🔄 Aguarda reload seguro após restauro de sessão
  private destroy$ = new Subject<void>();
  public isRestoringSession = true;

  @ViewChild('globalChatbot', { read: ChatbotComponent }) globalChatbot!: ChatbotComponent;

  /**
   * 🛡️ Verifica se o utilizador está num fluxo crítico (compra, simulação, questionário)
   * onde um reload automático causaria perda de dados.
   * Quando true, o reload do Service Worker é adiado até o utilizador sair do fluxo.
   */
  private isUserInCriticalFlow(): boolean {
    const url = this.router.url?.toLowerCase() || '';

    // 1. Rotas críticas: seguros (simulação/compra), área de cliente com formulários
    const criticalRoutes = ['/seguros/', '/clientarea/'];
    const isOnCriticalRoute = criticalRoutes.some(route => url.includes(route));

    // 2. Estado do GlobalService: está num fluxo de compra activo?
    const isInPurchaseFlow = !!this.globalService.safesimulationsClass?.OpenpurchaseComponent;
    const isInQuizFlow = !!this.globalService.OpenComponent;
    const hasPurchaseSteps = this.globalService.selectedTabPurchaseSteps?.length > 0;

    return isOnCriticalRoute && (isInPurchaseFlow || isInQuizFlow || hasPurchaseSteps);
  }

  constructor(
    private commonService: CommonService,
    public globalService: GlobalService,
    private swUpdate: SwUpdate,
    public router: Router,
    private loaddefaultdataService: LoaddefaultdataService,
    public breakpointObserver: BreakpointObserver,
    private cacheService: CacheService,
    private renderer: Renderer2,
    private ngZone: NgZone,
    private bottomSheet: MatBottomSheet,
    private authService: AuthService,
    private baseService: BaseService,
    private storage: SafeStorageService,
    private titleService: Title,
    private activatedRoute: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private userActivityService: UserActivityService,
    public googleAnalyticsService: GoogleAnalyticsService,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _navigationLogger: NavigationLoggerService, // ✅ Inicializa logging de navegação (escuta NavigationEnd)
    private seoService: SeoService,
  ) {
    // O spinner é ativado no ngOnInit.
    this.breakpointObserver
      .observe([Breakpoints.XSmall, Breakpoints.Small, Breakpoints.Handset])
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        this.globalService.isMobileDevice =
          result.breakpoints[Breakpoints.XSmall] ||
          result.breakpoints[Breakpoints.Small] ||
          result.breakpoints[Breakpoints.Handset];
      });
  }

  async initializeSession() {
    // 🛡️ Fallback para rotas antigas com '#'
    // Se o cliente usar um link antigo (ex: /#/intermediaryarea), redirecionar corretamente
    if (typeof window !== 'undefined' && window.location.hash && window.location.hash.startsWith('#/')) {
      const newPath = window.location.hash.substring(1); // remove o '#' e fica '/intermediaryarea'
      window.history.replaceState(null, '', newPath);
      this.router.navigateByUrl(newPath);
    }

    // 1. Iniciar o estado de carregamento, defaults de visibilidade de rotas e sinalizar o início do restauro
    this.globalService.routesConfigGlobal.HideHome = false;
    this.globalService.routesConfigGlobal.HideSimulations = true;
    this.globalService.routesConfigGlobal.HidePolicies = true;
    this.globalService.routesConfigGlobal.HideInsuranceclaims = true;
    this.globalService.routesConfigGlobal.HideContacts = false;
    this.globalService.routesConfigGlobal.HideSitemanagement = true;
    this.globalService.routesConfigGlobal.HideCommercialarea = true;
    this.globalService.routesConfigGlobal.HideClientarea = true;
    this.globalService.activeRequests = 0;

    this.globalService.isRestoringSession$.next(true);
    this.isRestoringSession = true;
    this.globalService.loadingSpinner = true;
    this.globalService.loadingMessage = 'A carregar dados...';

    // ⏱️ Timeout de segurança: garante que o spinner nunca fica preso por mais de 12s
    // 12s dá margem para redes 4G lentas (zonas rurais, dentro de edifícios)
    const safetyTimeout = setTimeout(() => {
      if (this.isRestoringSession) {
        console.warn('⚠️ [AppComponent] Safety timeout atingido — a forçar fim do carregamento.');
        this.isRestoringSession = false;
        this.globalService.isRestoringSession$.next(false);
        this.globalService.loadingSpinner = false;
        this.cdr.detectChanges();
      }
    }, 12000);

    try {
      // 2. Tentar restaurar a sessão persistente do localStorage
      const restoredUser = this.authService.restoreSession();

      if (restoredUser) {
        this.globalService.loggedUserClass = restoredUser;

        // Iniciar WebSockets Activity Tracking
        if (restoredUser.Token) {
          this.userActivityService.connect(restoredUser.Token);
        }

        // Reconstruir o estado das rotas com base no perfil do utilizador
        this.baseService.configRoutes();

        // 🚀 CARREGAMENTO EM PARALELO (Parallel Loading)
        // Em vez de esperar por um de cada vez, disparamos todos e esperamos pelo fim do conjunto.
        // Isto reduz drasticamente o tempo de bloqueio em redes móveis (4G/5G).
        const startupTasks: Promise<any>[] = [
          this.loaddefaultdataService.loadMaritalStatus(),
          this.loaddefaultdataService.loadProfessionalsituation(),
          this.loaddefaultdataService.loadDefaultTables(),
          this.loaddefaultdataService.loadProductsAll(),
        ];

        if (restoredUser.idUser) {
          startupTasks.push(this.loaddefaultdataService.loadCustomerUser(restoredUser.idUser));
        }

        await Promise.all(startupTasks);

        // Carregar dados específicos após ter o contexto básico
        this.loaddefaultdataService.loadMyInsuranceClaimsClass();
        this.storage.setLocal('isLoggedin', 'true');
      } else {
        console.warn('⚠️ [AppComponent] restoreSession: Nenhuma sessão ativa encontrada.');
        // Carregar dados globais para utilizadores não autenticados (Homepage)
        await Promise.all([
          this.loaddefaultdataService.loadDefaultTables(),
          this.loaddefaultdataService.loadProductsAll(),
        ]);
      }
    } catch (error) {
      console.error('❌ [AppComponent] Erro ao restaurar sessão:', error);
      this.authService.logout(false);
      this.globalService.loggedUserClass = new LoggedUserClass();
      this.storage.removeLocal('isLoggedin');
      // Tentar carregar pelo menos os dados básicos para a home não quebrar
      await Promise.all([
        this.loaddefaultdataService.loadDefaultTables(),
        this.loaddefaultdataService.loadProductsAll(),
      ]).catch(() => {});
    } finally {
      clearTimeout(safetyTimeout);

      // 3. Finalizar o estado de restauro
      // ✅ Solução para o erro NG0100: O setTimeout garante que a mudança de estado
      // ocorra após o ciclo de verificação atual do Angular.
      setTimeout(() => {
        // ✅ Melhoria de UX no ecrã azul:
        // Mantém o spinner bem visível com uma mensagem amigável antes do reload final
        if (this.pendingSwUpdate) {
          this.globalService.loadingMessage = 'A aplicar atualizações...';
          this.cdr.detectChanges();
          console.log('[SW] 🚀 Reload do update a ser executado após arranque completo.');
          // Pequeno delay apenas para a interface desenhar a nova mensagem do spinner
          setTimeout(() => location.reload(), 300);
          return; // ⛔ Evita ocultar o spinner e não faz `isRestoringSession = false`
        }

        this.isRestoringSession = false;
        this.globalService.isRestoringSession$.next(false);
        this.globalService.loadingSpinner = false;

        // Forçar a detecção de alterações para atualizar o template imediatamente
        this.cdr.detectChanges();
      });
    }
  }

  initDynamicTitle() {
    const appTitle = 'Seguros Virtual - O Futuro dos Seguros Começa Aqui';

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        map((event: NavigationEnd) => {
          // 🔍 SEO: Aplicar robots tag centralizada em TODAS as rotas
          // Apenas as rotas do sitemap.xml devem ser indexadas: / e /seguros/*
          const path = event.urlAfterRedirects.split('?')[0].split('#')[0];
          const isIndexable = path === '/' || path === '' || path.startsWith('/seguros');
          if (isIndexable) {
            this.seoService.setIndex();
          } else {
            this.seoService.setNoIndex();
          }

          let child = this.activatedRoute.firstChild;
          while (child?.firstChild) {
            child = child.firstChild;
          }
          if (child?.snapshot.data['title']) {
            return child.snapshot.data['title'] + ' | Seguros Virtual';
          }
          return appTitle;
        }),
      )
      .subscribe((ttl: string) => {
        this.titleService.setTitle(ttl);
      });
  }

  /**
   * 🛡️ Monitoriza a navegação para detetar quando o utilizador sai de um fluxo crítico.
   * Quando sai, aplica o reload pendente do Service Worker de forma segura.
   * ⏱️ Timeout máximo de 15 minutos: se o cliente permanecer no fluxo crítico
   * por mais de 15 min, o reload é forçado para garantir que updates graves são aplicados.
   */
  private monitorCriticalFlowExit(): void {
    // 🛡️ Monitorizar navegação — reload quando sair do fluxo crítico
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        filter(() => this.pendingSwUpdate && !this.isUserInCriticalFlow()),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        console.log('[SW] ✅ Cliente saiu do fluxo crítico — a aplicar atualização pendente.');
        this.applySafeReload();
      });

    // ⏱️ Timeout de segurança: forçar reload após 15 minutos máx de adiamento
    // Garante que atualizações graves são sempre aplicadas, mesmo que o cliente
    // permanece no fluxo de compra por muito tempo
    setTimeout(
      () => {
        if (this.pendingSwUpdate) {
          console.log('[SW] ⏱️ Timeout máximo de adiamento atingido (15 min) — a forçar atualização.');
          this.applySafeReload();
        }
      },
      15 * 60 * 1000,
    );
  }

  /** Aplica o reload com feedback visual */
  private applySafeReload(): void {
    this.globalService.loadingMessage = 'A aplicar atualizações...';
    this.globalService.loadingSpinner = true;
    this.cdr.detectChanges();
    setTimeout(() => location.reload(), 300);
  }

  /**
   * 🔄 Monitoriza a versão da aplicação em tempo real via /assets/version.txt
   * - Verifica periodicamente (a cada 3 min) em segundo plano
   * - Verifica imediatamente quando a janela/separador volta a ter foco ou visibilidade
   */
  private initVersionChecker(): void {
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          this.checkAppVersion();
        }
      });
      window.addEventListener('focus', () => {
        this.checkAppVersion();
      });
    }

    // Polling regular a cada 3 minutos (fora da zona Angular para não acionar Change Detection desnecessária)
    this.ngZone.runOutsideAngular(() => {
      setInterval(
        () => {
          this.checkAppVersion();
        },
        3 * 60 * 1000,
      );
    });

    // Verificação inicial rápida (10s após bootstrap)
    setTimeout(() => {
      this.checkAppVersion();
    }, 10000);
  }

  private checkAppVersion(): void {
    const t = Date.now();
    fetch(`/assets/version.txt?t=${t}`, { cache: 'no-store' })
      .then(res => (res.ok ? res.text() : Promise.reject('Sem version.txt')))
      .then(remoteHash => {
        if (!remoteHash) return;
        remoteHash = remoteHash.trim();
        const storedHash = localStorage.getItem('build_hash');

        if (storedHash && remoteHash && storedHash !== remoteHash) {
          console.log(`[VersionChecker] 🔄 Nova versão detetada: ${storedHash} -> ${remoteHash}`);
          localStorage.setItem('build_hash', remoteHash);
          this.ngZone.run(() => {
            if (this.isUserInCriticalFlow()) {
              console.log('[VersionChecker] 🛡️ Utilizador em fluxo crítico — reload adiado.');
              this.pendingSwUpdate = true;
              this.monitorCriticalFlowExit();
            } else {
              this.applySafeReload();
            }
          });
        } else if (!storedHash && remoteHash) {
          localStorage.setItem('build_hash', remoteHash);
        }
      })
      .catch(() => {
        // Silencioso em caso de erro momentâneo de rede
      });
  }

  private finalizarCarregamento(): void {
    this.getDefaults().subscribe(() => {
      this.globalService.loadingSpinner = false;
    });
  }

  ngOnInit(): void {
    // 🔄 Monitorização contínua de versões pós-deploy
    this.initVersionChecker();

    // 🌍 SEO: Dynamic Title Management
    this.initDynamicTitle();

    // Iniciar sessão de forma assíncrona para não bloquear renderização inicial
    this.initializeSession();

    // 🤖 Escutar evento global de abertura/fecho do chatbot
    this.globalService.toggleChatbot$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.globalChatbot) {
        this.globalChatbot.toggle();
      }
    });

    // ⚠️ Ativa o spinner imediatamente
    this.globalService.loadingSpinner = true;

    // 🛠️ Service Worker: detetar atualizações
    if (this.swUpdate.isEnabled) {
      // 🧭 Monitorizar todos os eventos de atualização
      this.swUpdate.versionUpdates.subscribe(event => {
        switch (event.type) {
          case 'VERSION_DETECTED':
            break;

          case 'VERSION_INSTALLATION_FAILED':
            // console.error(`[SW] ❌ Falha na instalação da versão ${event.version.hash}`, event.error);
            break;

          case 'VERSION_READY':
            // ✅ CORREÇÃO ECRÃ AZUL: Não fazer reload imediato durante o arranque sem feedback.
            // 🛡️ PROTEÇÃO DE COMPRA: Não fazer reload se o cliente está a preencher dados.
            this.swUpdate.activateUpdate().then(() => {
              if (this.isRestoringSession) {
                // Sessão ainda a carregar → adiar reload para depois do arranque
                console.log('[SW] 🔄 Update disponível — a aguardar fim do arranque para recarregar.');
                this.pendingSwUpdate = true;
              } else if (this.isUserInCriticalFlow()) {
                // 🛡️ Cliente em fluxo de compra/simulação → NÃO interromper!
                // O reload será feito quando o cliente navegar para uma rota segura
                console.log('[SW] 🛡️ Update pronto mas cliente em fluxo crítico — reload adiado.');
                this.pendingSwUpdate = true;
                this.monitorCriticalFlowExit();
              } else {
                // Sessão restaurada e rota segura → reload imediato com feedback
                this.globalService.loadingMessage = 'A aplicar atualizações...';
                this.globalService.loadingSpinner = true;
                this.cdr.detectChanges();
                setTimeout(() => location.reload(), 300);
              }
            });
            break;

          case 'NO_NEW_VERSION_DETECTED':
            break;
        }
      });

      // 🔁 Verificação periódica (30 minutos)
      // 30 min é o padrão enterprise — poupa bateria e dados móveis dos clientes
      // O SW já deteta updates automaticamente no arranque, este é apenas um fallback
      if (environment.production) {
        this.ngZone.runOutsideAngular(() => {
          setInterval(
            () => {
              this.swUpdate
                .checkForUpdate()
                .then(updateAvailable => {
                  if (updateAvailable) {
                    this.swUpdate.activateUpdate().then(() => {
                      this.ngZone.run(() => {
                        if (this.isUserInCriticalFlow()) {
                          // 🛡️ Cliente em fluxo de compra — adiar reload
                          console.log('[SW] 🛡️ Update periódico pronto mas cliente em fluxo crítico — reload adiado.');
                          this.pendingSwUpdate = true;
                          this.monitorCriticalFlowExit();
                        } else {
                          this.globalService.loadingMessage = 'A aplicar atualizações...';
                          this.globalService.loadingSpinner = true;
                          this.cdr.detectChanges();
                          setTimeout(() => location.reload(), 300);
                        }
                      });
                    });
                  }
                })
                .catch(err => {
                  console.error('[SW] ⚠️ Erro ao verificar atualizações:', err);
                });
            },
            30 * 60 * 1000,
          );
        });
      }

      // ✅ Verificação inicial
      // NOTA: finalizarCarregamento() é SEMPRE chamado independentemente de haver update.
      // O reload (quando há update) é feito APÓS o spinner ser escondido, para o utilizador
      // não ver o ecrã azul parado. O VERSION_READY (acima) trata do reload automático.
      this.swUpdate
        .checkForUpdate()
        .then(_updateAvailable => {
          this.finalizarCarregamento();
        })
        .catch(_ => {
          this.finalizarCarregamento();
        });
    } else {
      this.finalizarCarregamento();
    }

    // ✅ Check Cookie Consent Global
    const consent = this.storage.getLocal('cookieConsent');

    // 🤖 Detetar se é bot (Googlebot, Playwright, HeadlessChrome)
    // O bot nunca deve ver a janela de cookies, para não gravar o HTML estático com a janela aberta!
    const isBot =
      typeof navigator !== 'undefined' &&
      /bot|googlebot|crawler|spider|robot|crawling|headlesschrome|playwright/i.test(navigator.userAgent);

    if (!consent && typeof window !== 'undefined' && !isBot) {
      setTimeout(async () => {
        const { CookiesalertComponent } = await import('./shared/components/cookiesalert/cookiesalert.component');
        const config: MatBottomSheetConfig = {
          hasBackdrop: true,
          disableClose: true,
          panelClass: 'bottom-sheet-container',
          ariaLabel: 'Aviso de Cookies e Privacidade',
        };
        this.bottomSheet.open(CookiesalertComponent, config);
      }, 2000); // Small delay to allow app stable load
    } else if (consent && typeof window !== 'undefined') {
      // Re-apply usage (optional, if we want to ensure GTM gets the update event on every reload)
      if (typeof (window as any).gtag === 'function') {
        const status = consent === 'granted' ? 'granted' : 'denied';
        (window as any).gtag('consent', 'update', {
          ad_storage: status,
          ad_user_data: status,
          ad_personalization: status,
          analytics_storage: status,
        });
      }
    }

    if (typeof document !== 'undefined') {
      document.body.addEventListener('touchstart', this.handleTouchStart, { passive: true });
    }
  }

  clearBrowserData(): void {
    // ✅ Limpar apenas cache HTTP (não apagar todos os cookies)
    // Os cookies devem ser geridos individualmente quando necessário
    this.cacheService.clearCache();
  }

  loadScript(src: string, defer: boolean = false): void {
    if (typeof document !== 'undefined') {
      const script = this.renderer.createElement('script');
      script.src = src;
      script.async = true;
      if (defer) script.defer = true;
      this.renderer.appendChild(document.body, script);
    }
  }

  @HostListener('window:beforeunload', ['$event'])
  clearCacheOnWindowClose(event: BeforeUnloadEvent): void {
    // Só mostrar o alerta se não for update e se não for rota pública
    if (!this.updateAvailable && !this.router.url.startsWith('/login') && !this.router.url.startsWith('/')) {
      event.preventDefault();
      event.returnValue = ''; // ativa o alerta
    } else {
    }
  }

  @HostListener('window:touchstart', ['$event'])
  onTouchStart(_event: TouchEvent): void {
    document.body.classList.add('disable-scroll');
  }

  handleTouchStart(_e: TouchEvent) {
    // if (e.cancelable) e.preventDefault(); // Passive listener cannot preventDefault
  }

  getDefaults(): Observable<CommonDefaultsClass> {
    return this.commonService.GetCommonDefaults().pipe(
      tap(dados => {
        // dados is returned as an object from common-controller.js
        this.globalService.commonDefaultsClass = Array.isArray(dados) ? dados[0] : dados;
      }),
      catchError(_error => {
        console.warn(
          '⚠️ [AppComponent] Não foi possível carregar GetCommonDefaults. Funcionalidades de IA poderão estar indisponíveis.',
        );
        return of(null);
      }),
    );
  }

  ngOnDestroy(): void {
    if (!this.destroy$.closed) {
      // Verificação adicionada
      this.destroy$.next();
      this.destroy$.complete();
    }
  }
}
